import { T } from '@tolgee/react';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { usePaywallCtaTracking } from '../../../hooks/usePaywallCtaTracking';
import {
  ArrowIcon,
  BodyText,
  Centered,
  ChatIcon,
  EmailForm,
  FooterDotDivider,
  FooterLinkRow,
  InlineThanksPanel,
  LinkButton,
  ModalBody,
  ModalShell,
  OrangeBtn,
  OrDivider,
  SectionHeading,
  StepCard,
  StepCardGrid,
  StickerHeader,
  ThanksBody,
  ThanksSticker,
  Tip,
} from './primitives';
import {
  isInternational,
  resolveStatRegion,
  StatBlock,
  StatRegion,
} from './stateSpecificStats';
import { INTEREST_FORM_URL, LEARN_MORE_URL, PRICING_URL } from './urls';

// ── Public API ────────────────────────────────────────────────────────────

/**
 *  - last-lesson   Shown on lesson start when the user has 1 session left.
 *                  Replaces the old LastLessonConfirmation. US adds an email
 *                  capture below the confirmation buttons.
 *  - hard-block    Shown on lesson start when the user has 0 sessions left.
 *                  US: email form + form link. Intl: "See plans and pricing".
 *  - home          Shown when the teaching-session counter in the sidebar is
 *                  clicked. Mirrors SchoolPlanTrialModal's State 4 layout.
 */
export type WeeklySessionLimitState = 'last-lesson' | 'hard-block' | 'home';

interface Props {
  visible: boolean;
  onClose: () => void;
  state: WeeklySessionLimitState;
  country: string | undefined;
  region: string | undefined;
  remaining?: number;
  weeklyRate?: number;
  renewsAt?: string;
  /** Where the modal was opened from, for CTA-click analytics. */
  source?: string;
  onStartLesson?: () => void;
  onEmailSubmit?: (email: string) => void;
}

const formatRenewalDate = (renewsAt?: string) => {
  if (!renewsAt) return 'Sunday';
  try {
    const d = new Date(renewsAt);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Sunday';
  }
};

export const WeeklySessionLimitModal: FunctionComponent<Props> = props => {
  const {
    visible,
    onClose,
    state,
    country,
    region,
    remaining,
    weeklyRate,
    renewsAt,
    source,
  } = props;
  const trackCta = usePaywallCtaTracking();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  // Reset the thanks view when the parent flips this modal between states
  // (`last-lesson` ↔ `home`). Otherwise a previously submitted email keeps
  // the thanks view pinned across what should be a fresh state.
  useEffect(() => {
    setSubmittedEmail(null);
  }, [state]);

  // Synchronously clear the thanks state on every close path (X button, mask
  // click, ESC key, or the "Back to lesson" CTA inside ThanksView). Without
  // this, the modal stays mounted by PaywallProvider and the next reopen
  // (e.g. clicking the sidebar credit-info counter) would render the stale
  // ThanksView instead of the primary view.
  const handleClose = useCallback(() => {
    setSubmittedEmail(null);
    onClose();
  }, [onClose]);

  const intl = isInternational(country);
  const statRegion: StatRegion = resolveStatRegion(region, country);
  const renewalDate = formatRenewalDate(renewsAt);
  const weeklyRateValue = weeklyRate ?? 2;

  const handleEmailSubmit = (email: string) => {
    props.onEmailSubmit?.(email);
    setSubmittedEmail(email);
  };

  const ctaCtx = { paywallType: 'teaching-sessions' as const, source, modalState: state };

  // Home renders an inline thanks panel inside HomeView so the sidebar
  // interaction never swaps in a second modal. last-lesson and hard-block
  // still get the dedicated thanks card (last-lesson stays celebratory with a
  // Start lesson CTA; hard-block uses the sympathetic shell).
  if (submittedEmail && state !== 'home') {
    return (
      <ThanksView
        visible={visible}
        onClose={handleClose}
        email={submittedEmail}
        source={source}
        onStartLesson={state === 'last-lesson' ? props.onStartLesson : undefined}
      />
    );
  }

  if (state === 'last-lesson') {
    return (
      <LastLessonView
        visible={visible}
        onClose={handleClose}
        intl={intl}
        renewalDate={renewalDate}
        weeklyRate={weeklyRateValue}
        onStart={() => props.onStartLesson?.()}
        onEmailSubmit={handleEmailSubmit}
      />
    );
  }

  if (state === 'hard-block') {
    return (
      <HardBlockView
        visible={visible}
        onClose={handleClose}
        intl={intl}
        weeklyRate={weeklyRateValue}
        renewalDate={renewalDate}
        onEmailSubmit={handleEmailSubmit}
        onLearnMore={() => trackCta('learn-more', ctaCtx)}
        onFillOutForm={() => trackCta('fill-out-form', ctaCtx)}
        onSeePricing={() => trackCta('see-pricing', ctaCtx)}
      />
    );
  }

  return (
    <HomeView
      visible={visible}
      onClose={handleClose}
      intl={intl}
      statRegion={statRegion}
      remaining={remaining}
      submittedEmail={submittedEmail}
      onEmailSubmit={handleEmailSubmit}
      onLearnMore={() => trackCta('learn-more', ctaCtx)}
      onFillOutForm={() => trackCta('fill-out-form', ctaCtx)}
      onSeePricing={() => trackCta('see-pricing', ctaCtx)}
    />
  );
};

// ── Last lesson view ──────────────────────────────────────────────────────

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const OutlineBtn = styled.button`
  flex: none;
  background: #fff;
  color: ${theme.colors.black};
  border: 2px solid ${theme.colors.black};
  border-radius: 10px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 900;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  transition: background 0.2s cubic-bezier(0.645, 0.045, 0.355, 1),
    color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${theme.colors.black};
    color: #fff;
  }
`;

const Divider = styled.div`
  margin: 16px 0 12px;
  height: 1px;
  background: ${theme.colors.beigeDark};
`;

const SubText = styled.p`
  font-size: 12px;
  color: rgba(40, 47, 51, 0.7);
  margin: 0 0 10px;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
`;

interface LastLessonProps {
  visible: boolean;
  onClose: () => void;
  intl: boolean;
  renewalDate: string;
  weeklyRate: number;
  onStart: () => void;
  onEmailSubmit: (email: string) => void;
}

const LastLessonView: FunctionComponent<LastLessonProps> = ({
  visible,
  onClose,
  intl,
  renewalDate,
  weeklyRate,
  onStart,
  onEmailSubmit,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={540}>
    <StickerHeader
      sticker="clap"
      tone="beige"
      badgeIcon="gift"
      badge={
        <T keyName="WeeklySession.Badge.OneLeft" defaultValue="1 teaching session left" />
      }
      heading={
        <T
          keyName="WeeklySession.LastLesson.Heading"
          defaultValue="Last lesson until {renewalDate}"
          params={{ renewalDate }}
        />
      }
    />
    <ModalBody>
      <BodyText>
        <T
          keyName="WeeklySession.LastLesson.Body"
          defaultValue="Your free plan includes {weeklyRate} teaching sessions per week. This is the last one until {renewalDate}. If you start it, you can still use this lesson with other classes this week."
          params={{ weeklyRate, renewalDate }}
        />
      </BodyText>

      <ButtonRow>
        <div style={{ flex: 1 }}>
          <OrangeBtn onClick={onStart}>
            <T keyName="WeeklySession.LastLesson.Start" defaultValue="Start lesson" />
            <ArrowIcon />
          </OrangeBtn>
        </div>
        <OutlineBtn onClick={onClose}>
          <T keyName="WeeklySession.LastLesson.NotNow" defaultValue="Not now" />
        </OutlineBtn>
      </ButtonRow>

      {!intl && (
        <>
          <Divider />
          <SubText>
            <T
              keyName="WeeklySession.LastLesson.EmailIntro"
              defaultValue="Want unlimited teaching sessions? Leave your email — we'll help your school get on a plan."
            />
          </SubText>
          <EmailForm onSubmit={onEmailSubmit} />
        </>
      )}

      <Tip>
        <T
          keyName="WeeklySession.LastLesson.Tip"
          defaultValue="<strong>Tip:</strong> Weekly sessions reset every Sunday. Use them with as many classes as you want."
          params={{ strong: <strong /> }}
        />
      </Tip>
    </ModalBody>
  </ModalShell>
);

// ── Hard block view (out of weekly sessions) ─────────────────────────────
//
// Same shape as SchoolPlanTrialModal's hard-block (lavender → orange tone for
// teaching-session palette, gift badge icon, clap sticker). Triggered when a
// freemium teacher tries to start a lesson and `hasReachedLessonLimit` is
// true. After email submit it transitions to the shared ThanksView.

interface HardBlockProps {
  visible: boolean;
  onClose: () => void;
  intl: boolean;
  weeklyRate: number;
  renewalDate: string;
  onEmailSubmit: (email: string) => void;
  onLearnMore: () => void;
  onFillOutForm: () => void;
  onSeePricing: () => void;
}

const HardBlockView: FunctionComponent<HardBlockProps> = ({
  visible,
  onClose,
  intl,
  weeklyRate,
  renewalDate,
  onEmailSubmit,
  onLearnMore,
  onFillOutForm,
  onSeePricing,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={520}>
    <StickerHeader
      sticker="clap"
      tone="orange"
      badgeIcon="gift"
      badge={
        <T
          keyName="WeeklySession.HardBlock.Badge"
          defaultValue="0 sessions left this week"
        />
      }
      heading={
        <T
          keyName="WeeklySession.Home.Heading"
          defaultValue="Get Unlimited Weekly Teaching Sessions"
        />
      }
    />
    <ModalBody>
      <BodyText>
        {intl ? (
          <T
            keyName="WeeklySession.HardBlock.Body.Intl"
            defaultValue="You've used your {weeklyRate} free teaching sessions this week. Sessions reset {renewalDate} — or upgrade to the school plan for unlimited teaching."
            params={{ weeklyRate, renewalDate }}
          />
        ) : (
          <T
            keyName="WeeklySession.HardBlock.Body.US"
            defaultValue="You've used your {weeklyRate} free teaching sessions this week. We would love to help you get unlimited access. Fill out the form or leave your email — we'll reach out."
            params={{ weeklyRate }}
          />
        )}
      </BodyText>

      {intl ? (
        <a href={PRICING_URL} style={{ textDecoration: 'none' }} onClick={onSeePricing}>
          <OrangeBtn>
            <T keyName="BlockedPaywall.SeePricing" defaultValue="See plans and pricing" />
            <ArrowIcon />
          </OrangeBtn>
        </a>
      ) : (
        <EmailForm onSubmit={onEmailSubmit} autoFocus />
      )}

      <FooterLinkRow>
        {!intl && (
          <>
            <LinkButton href={INTEREST_FORM_URL} target="_blank" onClick={onFillOutForm}>
              <T keyName="BlockedPaywall.FillOutForm" defaultValue="Fill out this form" />
            </LinkButton>
            <FooterDotDivider>·</FooterDotDivider>
          </>
        )}
        <LinkButton href={LEARN_MORE_URL} target="_blank" onClick={onLearnMore}>
          <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
        </LinkButton>
      </FooterLinkRow>

      <Tip>
        <T
          keyName="WeeklySession.HardBlock.Tip"
          defaultValue="<strong>Tip:</strong> Sessions reset every Sunday. Each session lets you teach the same lesson with as many classes as you want."
          params={{ strong: <strong /> }}
        />
      </Tip>
    </ModalBody>
  </ModalShell>
);

// ── Home view (sidebar click) ────────────────────────────────────────────

interface HomeProps {
  visible: boolean;
  onClose: () => void;
  intl: boolean;
  statRegion: StatRegion;
  remaining?: number;
  submittedEmail: string | null;
  onEmailSubmit: (email: string) => void;
  onLearnMore: () => void;
  onFillOutForm: () => void;
  onSeePricing: () => void;
}

const HomeView: FunctionComponent<HomeProps> = ({
  visible,
  onClose,
  intl,
  statRegion,
  remaining,
  submittedEmail,
  onEmailSubmit,
  onLearnMore,
  onFillOutForm,
  onSeePricing,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={580}>
    <StickerHeader
      sticker="rocket"
      tone="orange"
      badgeIcon="gift"
      badge={
        remaining != null && remaining === 1 ? (
          <T
            keyName="WeeklySession.Badge.OneLeft"
            defaultValue="1 teaching session left"
          />
        ) : remaining != null ? (
          <T
            keyName="WeeklySession.Badge.NLeft"
            defaultValue="{remaining} teaching sessions left"
            params={{ remaining }}
          />
        ) : (
          <T
            keyName="WeeklySession.Badge.OneLeft"
            defaultValue="1 teaching session left"
          />
        )
      }
      heading={
        <T
          keyName="WeeklySession.Home.Heading"
          defaultValue="Get Unlimited Weekly Teaching Sessions"
        />
      }
    />
    <ModalBody>
      <BodyText>
        {intl ? (
          <T
            keyName="WeeklySession.Home.Body.Intl"
            defaultValue="On the free plan, you can teach 2 lessons each week, with as many classes as you want. To get unlimited access, you need a school plan."
          />
        ) : (
          <T
            keyName="WeeklySession.Home.Body.US"
            defaultValue="On the free plan, you can teach 2 lessons each week, with as many classes as you want. To get unlimited access, you need a school plan — but we know that takes time. So we have a process to support teachers while their school isn't ready yet."
          />
        )}
      </BodyText>

      {!intl && (
        <>
          <StepCardGrid>
            <StepCard n={1}>
              <T
                keyName="BlockedPaywall.Step1.Invite"
                defaultValue="Invite your principal to observe the engagement in your classroom"
              />
            </StepCard>
            <StepCard n={2}>
              <T
                keyName="BlockedPaywall.Step2.FollowUp"
                defaultValue="We follow up and help you connect it to your school's goals and find budget"
              />
            </StepCard>
          </StepCardGrid>

          {!submittedEmail && (
            <SectionHeading>
              <T
                keyName="BlockedPaywall.HelpHeading"
                defaultValue="We can help you out with every step:"
              />
            </SectionHeading>
          )}
        </>
      )}

      {submittedEmail ? (
        <InlineThanksPanel email={submittedEmail} />
      ) : intl ? (
        <a href={PRICING_URL} style={{ textDecoration: 'none' }} onClick={onSeePricing}>
          <OrangeBtn>
            <T keyName="BlockedPaywall.SeePricing" defaultValue="See plans and pricing" />
            <ArrowIcon />
          </OrangeBtn>
        </a>
      ) : (
        <>
          <a
            href={INTEREST_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
            onClick={onFillOutForm}
          >
            <OrangeBtn>
              <T
                keyName="BlockedPaywall.FillOutFormCta"
                defaultValue="Fill out this form to get help"
              />
            </OrangeBtn>
          </a>
          <OrDivider>
            <T keyName="BlockedPaywall.Or" defaultValue="or" />
          </OrDivider>
          <EmailForm onSubmit={onEmailSubmit} />
        </>
      )}

      {!intl && (
        <div style={{ marginTop: 26 }}>
          <SectionHeading style={{ marginBottom: 14 }}>
            <T keyName="BlockedPaywall.DidYouKnow" defaultValue="Did you know?" />
          </SectionHeading>
          <StatBlock statRegion={statRegion} />
        </div>
      )}

      <Centered>
        <LinkButton href={LEARN_MORE_URL} target="_blank" onClick={onLearnMore}>
          <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
        </LinkButton>
      </Centered>
    </ModalBody>
  </ModalShell>
);

// ── Thanks view (after email submit) ─────────────────────────────────────

const ThanksHeading = styled.h2`
  font-size: 22px;
  font-weight: 900;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
  font-family: 'Nunito', sans-serif;
`;

const ThanksText = styled.p`
  font-size: 13px;
  color: rgba(40, 47, 51, 0.7);
  margin: 0 0 18px;
  line-height: 1.55;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;

  strong {
    color: #282f33;
    font-weight: 900;
  }
`;

const ThanksView: FunctionComponent<{
  visible: boolean;
  onClose: () => void;
  email: string;
  source?: string;
  onStartLesson?: () => void;
}> = ({ visible, onClose, email, source, onStartLesson }) => {
  // last-lesson — celebratory: the teacher can still teach this lesson, so the
  // tone is upbeat and the primary CTA starts the lesson.
  if (onStartLesson) {
    return (
      <ModalShell visible={visible} onClose={onClose} width={480}>
        <ThanksBody>
          <ThanksSticker src="/images/blocked-paywall-stickers/party.svg" alt="" />
          <ThanksHeading>
            <T keyName="BlockedPaywall.OnIt.Heading" defaultValue="We're on it!" />
          </ThanksHeading>
          <ThanksText>
            <T
              keyName="BlockedPaywall.OnIt.Body"
              defaultValue="We'll be in touch at <strong>{email}</strong> within 1 business day to help get your school on a plan 🙂"
              params={{ email, strong: <strong /> }}
            />
          </ThanksText>
          <ButtonRow style={{ justifyContent: 'center' }}>
            <div style={{ flex: 1 }}>
              <OrangeBtn onClick={onStartLesson}>
                <T keyName="WeeklySession.OnIt.StartLesson" defaultValue="Start lesson" />
                <ArrowIcon />
              </OrangeBtn>
            </div>
            <OutlineBtn onClick={onClose}>
              <T keyName="BlockedPaywall.OnIt.Back" defaultValue="Back to lesson" />
            </OutlineBtn>
          </ButtonRow>
        </ThanksBody>
      </ModalShell>
    );
  }

  // hard-block — sympathetic: the teacher is fully blocked from playing this
  // lesson. Mirrors SchoolPlanTrialModal's ThanksView structure so both
  // blocked-state thanks views feel equally warm.
  return (
    <SympatheticThanksView
      visible={visible}
      onClose={onClose}
      email={email}
      source={source}
    />
  );
};

const SympatheticThanksView: FunctionComponent<{
  visible: boolean;
  onClose: () => void;
  email: string;
  source?: string;
}> = ({ visible, onClose, email, source }) => {
  const { show, showNewMessages } = useIntercom();
  const trackCta = usePaywallCtaTracking();
  const ctaCtx = {
    paywallType: 'teaching-sessions' as const,
    source,
    modalState: 'hard-block-thanks',
  };

  // Close the modal first, then open the Intercom messenger. The antd modal
  // mask sits at z-index 1000+ and on lesson/preview routes the launcher is
  // hidden via `useHideIntercom`, so calling `showNewMessages` while the
  // modal is still mounted leaves the messenger occluded (and on some routes
  // never visible at all). Closing first guarantees the user sees the chat.
  const handleChat = () => {
    trackCta('chat-with-us', ctaCtx);
    onClose();
    show();
    showNewMessages();
  };

  return (
    <ModalShell visible={visible} onClose={onClose} width={520}>
      <StickerHeader
        sticker="praise"
        tone="cream"
        badgeIcon="gift"
        badge={<T keyName="BlockedPaywall.Thanks.Badge" defaultValue="Thank you" />}
        heading={
          <T
            keyName="BlockedPaywall.Thanks.Heading"
            defaultValue="We'll be in touch soon"
          />
        }
      />
      <ModalBody>
        <BodyText>
          <T
            keyName="BlockedPaywall.Thanks.Body"
            defaultValue="We're so sorry if this interferes with your teaching. We'll reach out to <strong>{email}</strong> soon. Chat with us if you need help now."
            params={{
              email,
              strong: <strong style={{ color: '#E58332' }} />,
            }}
          />
        </BodyText>
        <OrangeBtn dark onClick={handleChat}>
          <ChatIcon />
          <T keyName="BlockedPaywall.Thanks.Chat" defaultValue="Chat with us now" />
        </OrangeBtn>
        <FooterLinkRow>
          <LinkButton
            href={INTEREST_FORM_URL}
            target="_blank"
            onClick={() => trackCta('fill-out-form', ctaCtx)}
          >
            <T keyName="BlockedPaywall.FillOutForm" defaultValue="Fill out this form" />
          </LinkButton>
          <FooterDotDivider>·</FooterDotDivider>
          <LinkButton
            href={LEARN_MORE_URL}
            target="_blank"
            onClick={() => trackCta('learn-more', ctaCtx)}
          >
            <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
          </LinkButton>
        </FooterLinkRow>
        <Tip>
          <T
            keyName="BlockedPaywall.Tip.Principal"
            defaultValue="<strong>Tip:</strong> Invite your principal to observe a lesson. That's what gets their interest."
            params={{ strong: <strong /> }}
          />
        </Tip>
      </ModalBody>
    </ModalShell>
  );
};
